"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLocaleContext } from "@/components/i18n/locale-context";
import { interpolate } from "@/lib/i18n/dictionaries";
import { toast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { ImageAttachmentPicker } from "@/components/help/image-attachment-picker";

type ContactSession =
  | { status: "loading" }
  | { status: "ready"; authenticated: false }
  | { status: "ready"; authenticated: true; email: string | null; fullName: string | null };

type Props = {
  /** e.g. help (default), settings, or contact-public — stored for owner triage */
  source?: string;
  className?: string;
  /** Anchor id for in-page links (e.g. help escalation banner → #help-support-form) */
  anchorId?: string;
  /** Always show name + email fields even when signed in (for the public contact page). */
  forceGuest?: boolean;
};

const guestEmailCheck = z.string().trim().toLowerCase().email();

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden="true">
      *
    </span>
  );
}

export function ContactSupportForm({ source = "help", className, anchorId, forceGuest }: Props) {
  const { t } = useLocaleContext();
  const [session, setSession] = useState<ContactSession>({ status: "loading" });
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/support/contact-session", { credentials: "include" });
        const data = (await res.json().catch(() => ({}))) as {
          authenticated?: unknown;
          email?: unknown;
          fullName?: unknown;
        };
        if (cancelled) return;
        if (data.authenticated === true) {
          setSession({
            status: "ready",
            authenticated: true,
            email: typeof data.email === "string" ? data.email : null,
            fullName: typeof data.fullName === "string" ? data.fullName : null,
          });
        } else {
          setSession({ status: "ready", authenticated: false });
        }
      } catch {
        if (!cancelled) setSession({ status: "ready", authenticated: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pagePath = typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : null;

  const showGuestFields = forceGuest || (session.status === "ready" && !session.authenticated);

  const readyToSend = useMemo(() => {
    if (body.trim().length < 10) return false;
    if (session.status !== "ready") return false;
    if (showGuestFields) {
      const nameOk = guestName.trim().length >= 1 && guestName.trim().length <= 120;
      const emailOk = guestEmailCheck.safeParse(guestEmail).success;
      return nameOk && emailOk;
    }
    return true;
  }, [body, session, guestName, guestEmail, showGuestFields]);

  const submit = useCallback(async () => {
    const text = body.trim();
    if (text.length < 10 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const form = new FormData();
      form.set("body", text);
      form.set("subject", subject.trim());
      form.set("pagePath", pagePath ?? "");
      form.set("source", source);
      if (showGuestFields) {
        form.set("guestName", guestName.trim());
        form.set("guestEmail", guestEmail.trim().toLowerCase());
      }
      for (const f of files) {
        form.append("attachments", f);
      }
      const res = await fetch("/api/support/contact", { method: "POST", body: form });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) {
        setError(typeof data.error === "string" ? data.error : t("help.contactForm.failedTitle"));
        return;
      }
      setDone(true);
      setBody("");
      setSubject("");
      setFiles([]);
      setGuestName("");
      setGuestEmail("");
    } catch {
      setError(t("help.contactForm.failedTitle"));
    } finally {
      setSubmitting(false);
    }
  }, [body, subject, files, pagePath, source, submitting, t, session, guestName, guestEmail]);

  if (done) {
    return (
      <div className={cn("rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm", className)} role="status">
        <p className="font-medium text-emerald-950 dark:text-emerald-100">{t("help.contactForm.successTitle")}</p>
        <p className="mt-1 text-muted-foreground">
          {showGuestFields
            ? t("help.contactForm.successBodyGuest")
            : t("help.contactForm.successBody")}
        </p>
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setDone(false)}>
          {t("help.contactForm.sendAnother")}
        </Button>
      </div>
    );
  }

  return (
    <div id={anchorId} className={cn("scroll-mt-28 space-y-3", className)}>
      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      {session.status === "loading" ? (
        <p className="text-sm text-muted-foreground">{t("common.loadingEllipsis")}</p>
      ) : showGuestFields ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="contact-guest-name" className="inline-flex items-center gap-0.5">
              {t("contactPage.guestNameLabel")}
              <RequiredMark />
            </Label>
            <Input
              id="contact-guest-name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder={t("contactPage.guestNamePlaceholder")}
              maxLength={120}
              disabled={submitting}
              autoComplete="name"
              aria-required="true"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contact-guest-email" className="inline-flex items-center gap-0.5">
              {t("contactPage.guestEmailLabel")}
              <RequiredMark />
            </Label>
            <Input
              id="contact-guest-email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder={t("contactPage.guestEmailPlaceholder")}
              maxLength={320}
              disabled={submitting}
              autoComplete="email"
              aria-required="true"
            />
          </div>
        </div>
      ) : session.authenticated ? (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-foreground">
          {interpolate(t("contactPage.signedInNote"), {
            name:
              session.fullName?.trim() ||
              (session.email?.includes("@") ? session.email.split("@")[0] : "") ||
              "—",
            email: session.email ?? "—",
          })}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="support-subject" className="text-muted-foreground">
          {t("help.contactForm.subjectLabel")}
        </Label>
        <Input
          id="support-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder={t("help.contactForm.subjectPlaceholder")}
          maxLength={200}
          disabled={submitting}
          autoComplete="off"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="support-body" className="inline-flex items-center gap-0.5">
          {t("help.contactForm.messageLabel")}
          <RequiredMark />
        </Label>
        <Textarea
          id="support-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t("help.contactForm.messagePlaceholder")}
          rows={5}
          disabled={submitting}
          maxLength={8000}
          className="min-h-[120px] resize-y text-base sm:text-sm"
          aria-required="true"
        />
        <p className="text-xs text-muted-foreground">{t("help.contactForm.hint")}</p>
      </div>
      <ImageAttachmentPicker
        files={files}
        onFilesChange={setFiles}
        disabled={submitting}
        label={t("help.contactForm.attachmentsLabel")}
        hint={t("help.contactForm.attachmentsHint")}
        dropTitle={t("help.attachments.dropTitle")}
        dropSubtitle={t("help.attachments.dropSubtitle")}
        removePhotoLabel={t("help.attachments.removePhoto")}
        onValidationError={(code) =>
          toast({
            title: code === "fileTooLarge" ? t("help.bugReport.fileTooLarge") : t("help.bugReport.fileTypeInvalid"),
            variant: "destructive",
          })
        }
      />
      <Button
        type="button"
        variant={readyToSend || submitting ? "default" : "secondary"}
        className="w-full sm:w-auto"
        disabled={submitting || !readyToSend || session.status === "loading"}
        onClick={() => void submit()}
      >
        {submitting ? (
          t("help.contactForm.submitting")
        ) : (
          <>
            <Send className="mr-2 h-4 w-4" />
            {t("help.contactForm.submit")}
          </>
        )}
      </Button>
    </div>
  );
}
